import React from 'react';
import TransformSection from './common/transform-section';
import { registerInspector } from './inspector-registry';
import ColorField from './shared/color-field';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import NumberField from './shared/number-field';
import SelectField from './shared/select-field';
import TextField from './shared/text-field';
import { useFabricProp } from './shared/use-fabric-prop';
import type { ObjectInspectorProps } from './types';

const FONT_FAMILIES = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Verdana', label: 'Verdana' },
];

const FONT_WEIGHTS = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Bold' },
  { value: '300', label: 'Light (300)' },
  { value: '500', label: 'Medium (500)' },
  { value: '700', label: 'Bold (700)' },
];

const TEXT_ALIGNS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
  { value: 'justify', label: 'Justify' },
];

const TextboxInspector: React.FC<ObjectInspectorProps> = ({ object, editor }) => {
  const [text, setText] = useFabricProp<string>(object, 'text', editor);
  const [fontSize, setFontSize] = useFabricProp<number>(object, 'fontSize', editor);
  const [fontFamily, setFontFamily] = useFabricProp<string>(object, 'fontFamily', editor);
  const [fontWeight, setFontWeight] = useFabricProp<string>(object, 'fontWeight', editor);
  const [textAlign, setTextAlign] = useFabricProp<string>(object, 'textAlign', editor);
  const [lineHeight, setLineHeight] = useFabricProp<number>(object, 'lineHeight', editor);
  const [fill, setFill] = useFabricProp<string>(object, 'fill', editor);
  const [opacity, setOpacity] = useFabricProp<number>(object, 'opacity', editor);

  return (
    <>
      <TransformSection object={object} editor={editor} />
      <InspectorSection title="Content">
        <InspectorRow label="Text">
          <TextField value={text} onChange={setText} multiline />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title="Typography">
        <InspectorRow label="Family">
          <SelectField value={fontFamily} onChange={setFontFamily} options={FONT_FAMILIES} />
        </InspectorRow>
        <InspectorRow label="Size">
          <NumberField value={fontSize} onChange={setFontSize} min={1} />
        </InspectorRow>
        <InspectorRow label="Weight">
          <SelectField
            value={String(fontWeight)}
            onChange={setFontWeight}
            options={FONT_WEIGHTS}
          />
        </InspectorRow>
        <InspectorRow label="Align">
          <SelectField value={textAlign} onChange={setTextAlign} options={TEXT_ALIGNS} />
        </InspectorRow>
        <InspectorRow label="Line H">
          <NumberField value={lineHeight} onChange={setLineHeight} step={0.1} min={0.1} />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title="Appearance">
        <InspectorRow label="Color">
          <ColorField value={fill} onChange={setFill} />
        </InspectorRow>
        <InspectorRow label="Opacity">
          <NumberField value={opacity} onChange={setOpacity} step={0.05} min={0} max={1} />
        </InspectorRow>
      </InspectorSection>
    </>
  );
};

export default TextboxInspector;

registerInspector('textbox', TextboxInspector);
registerInspector('i-text', TextboxInspector);
registerInspector('text', TextboxInspector);
